<template>
  <v-card class="pa-4">
    <v-row align="center">
      <v-col cols="6">
        <span class="font-weight-bold">
          {{ code }}
        </span>
      </v-col>

      <v-col cols="4">
        <order-status-label
          :status="status"
          :withholding-tax-status="withholdingTaxStatus"
          :deleted-type="deletedType"
          :is-juristic-customer="isJuristicCustomer"></order-status-label>
      </v-col>

      <v-col
        cols="2"
        class="text-end">
        <v-btn
          text
          small
          color="primary"
          @click="toggleIsExpanded">
          <v-icon
            v-if="isExpanded"
            right>
            mdi-chevron-up
          </v-icon>
          <v-icon
            v-else
            right>
            mdi-chevron-down
          </v-icon>
        </v-btn>
      </v-col>
    </v-row>

    <template v-if="isExpanded">
      <v-row
        v-if="isRejectStatus && rejectReason"
        no-gutters>
        <v-col cols="auto">
          <div class="red--text mr-1">
            ***
          </div>
        </v-col>
        <v-col>
          <span class="error--text _fs-14">
            หมายเหตุ {{ rejectReason }}
          </span>
        </v-col>
      </v-row>

      <v-row
        v-if="isDeletedStatus && !isCancelByUser"
        no-gutters>
        <v-col cols="auto">
          <div class="red--text mr-1">
            ***
          </div>
        </v-col>
        <v-col>
          <span class="error--text _fs-14">
            หมายเหตุ {{ deleteReason || "รายการสั่งซื้อหมดอายุ เนื่องจากไม่มีการชำระภายใน 30 วันตามกำหนด" }}
          </span>
        </v-col>
      </v-row>

      <v-row
        v-if="shouldDisplayWithholdingTaxUpload && withholdingTaxStatus === 'rejected'"
        no-gutters>
        <v-col cols="auto">
          <div class="red--text mr-1">
            ***
          </div>
        </v-col>
        <v-col>
          <span class="error--text _fs-14">
            หมายเหตุ {{ withholdingTaxRejectionReason || "เอกสารภาษีไม่ถูกต้อง" }}
          </span>
          <span
            class="green--text text-decoration-underline cursor-pointer _fs-14"
            @click="showFilePreviewDialog = true"
          >
            ดูรายละเอียดที่นี่
          </span>
        </v-col>
      </v-row>

      <div class="mt-4">
        รายการที่ซื้อ
      </div>
      <ul>
        <li
          v-for="orderItem in orderItems"
          :key="orderItem._id">
          {{ orderItem.productName }}
        </li>
      </ul>

      <div class="mt-4">
        วันที่ทำการซื้อ: {{ createdAt | dateTimeFormat("DD MMM YYYY - HH:mm") }}
      </div>
    </template>

    <div
      v-if="isExpanded"
      class="mt-4 d-flex justify-space-around flex-wrap">
      <v-btn
        v-if="['new', 'pending', 'rejected'].includes(status)"
        class="ma-4"
        color="primary"
        rounded
        outlined
        min-width="220"
        :to="{ name: 'OrderEdit', params: { productType: type, id: orderId } }"
      >
        <v-icon left>
          mdi-pencil
        </v-icon>
        แก้ไขออเดอร์
      </v-btn>
      <v-btn
        v-if="['new', 'pending', 'rejected'].includes(status)"
        class="ma-4"
        color="error"
        rounded
        outlined
        min-width="220"
        @click="$emit('delete', orderId, code)"
      >
        <v-icon left>
          mdi-delete
        </v-icon>
        ลบออเดอร์
      </v-btn>

      <v-btn
        v-if="status === 'new' || (status === 'rejected')"
        class="ma-4"
        color="primary"
        rounded
        min-width="220"
        :href="`/upload-slip/${orderId}`"
      >
        แจ้งชำระเงิน
      </v-btn>

      <v-btn
        v-if="shouldDisplayWithholdingTaxUpload"
        class="ma-4"
        color="primary"
        rounded
        min-width="220"
        @click="attachTaxDocument"
      >
        แนบใบภาษีหัก ณ ที่จ่าย
      </v-btn>

      <v-btn
        v-else-if="status !== 'new'"
        class="ma-4"
        color="primary"
        rounded
        outlined
        min-width="220"
        :disabled="shouldDisableWithholdingTaxDownload"
        :loading="$wait.is('downloading-receipt')"
        @click="$emit('view-tax-document', orderId, code)"
      >
        <v-icon left>
          mdi-file
        </v-icon>
        ใบกำกับภาษี
      </v-btn>

      <v-btn
        class="ma-4"
        color="primary"
        rounded
        outlined
        min-width="220"
        :to="{ name: 'OrderDetail', params: { id: orderId } }"
      >
        <v-icon left>
          mdi-file
        </v-icon>
        ใบเสนอราคา
      </v-btn>
    </div>

    <!-- Receipt Expiration Notice -->
    <div
      v-if="shouldDisplayWithholdingTaxDownload"
      class="mt-4 pa-3 grey lighten-5 d-flex flex-row">
      <div class="red--text mr-1">
        ***
      </div>
      <div class="text-caption red--text text--darken-1">
        หมายเหตุ ใบเสร็จรับเงิน/ใบกำกับภาษีมีอายุ 90 วัน หลังจากนั้นไม่สามารถดาวน์โหลดได้<br>
        สอบถามเพิ่มเติมที่ <a
          href="mailto:lineagency@sellsuki.com"
          class="red--text">lineagency@sellsuki.com</a> หรือ LINE @linebysellsuki
      </div>
    </div>

    <template v-if="isNewLineOfficialAccount || isTransferLineOfficialAccount">
      <div class="mt-4">
        ประเภทลูกค้า
        <div class="primary--text font-weight-bold">
          {{ newLineOfficialAccountTypeText }}
        </div>
      </div>
      <template v-if="isTransferLineOfficialAccount">
        <div class="mt-4">
          Basic ID:
          <span class="primary--text font-weight-bold">
            {{ basicId }}
          </span>
        </div>
        <div class="mt-4">
          ช่องทางที่เคยชำระเงินมาก่อน
          <div class="primary--text font-weight-bold">
            {{ transferAgencyPaymentTypeText }}
          </div>
        </div>
      </template>
    </template>

    <div class="mt-4">
      สร้างโดย:
      <span class="font-weight-bold">
        {{ createdBy }}
      </span>
    </div>

    <!-- File Preview Dialog -->
    <withholding-tax-files-dialog
      v-model="showFilePreviewDialog"
      :order-id="orderId"
    />
  </v-card>
</template>

<script>
import { mapState, mapGetters } from 'vuex'

export default {
  name: 'MobileOrderCard',
  components: {
    OrderStatusLabel: () => import('./OrderStatusLabel'),
    WithholdingTaxFilesDialog: () => import('@/components/WithholdingTaxFilesDialog')
  },
  props: {
    basicId: {
      type: String,
      default: ''
    },
    orderId: {
      type: String,
      default: ''
    },
    code: {
      type: String,
      default: ''
    },
    createdAt: {
      type: String,
      default: ''
    },
    orderItems: {
      type: Array,
      default: () => []
    },
    updatedAt: {
      type: String,
      default: ''
    },
    createdBy: {
      type: String,
      default: ''
    },
    status: {
      type: String,
      default: ''
    },
    rejectReason: {
      type: String,
      default: ''
    },
    deleteReason: {
      type: String,
      default: ''
    },
    withholdingTaxRejectionReason: {
      type: String,
      default: ''
    },
    type: {
      type: String,
      default: ''
    },
    isNewLineOfficialAccount: {
      type: Boolean,
      default: false
    },
    isTransferLineOfficialAccount: {
      type: Boolean,
      default: false
    },
    newLineOfficialAccountTypeText: {
      type: String,
      default: ''
    },
    transferAgencyPaymentTypeText: {
      type: String,
      default: ''
    },
    isJuristicCustomer: {
      type: Boolean,
      default: false
    },
    deletedType: {
      type: String,
      default: ''
    },
    withholdingTaxStatus: {
      type: String,
      default: ''
    },
    peakReceiptDocumentUrl: {
      type: String,
      default: ''
    }
  },
  data () {
    return {
      isExpanded: false,
      showFilePreviewDialog: false
    }
  },
  computed: {
    ...mapState('User', ['userProfile']),
    lineUid() {
      return this.userProfile?.uid || null
    },
    ...mapGetters({
      activeLineOfficialAccount: 'User/activeLineOfficialAccount'
    }),
    shouldDisplayWithholdingTaxFeature () {
      if (!['pending', 'completed', 'paid'].includes(this.status)) return false

      return true
    },
    shouldDisplayWithholdingTaxUpload () {
      if (!this.isJuristicCustomer) return false // Non-juristic never uploads
      if (!this.shouldDisplayWithholdingTaxFeature) return false
      if (['pending', 'completed'].includes(this.withholdingTaxStatus)) return false

      return true
    },
    shouldDisplayWithholdingTaxDownload () {
      if (!this.shouldDisplayWithholdingTaxFeature) return false
      if (this.shouldDisplayWithholdingTaxUpload) return false

      return true
    },
    shouldDisableWithholdingTaxDownload () {
      return this.status !== 'completed' || this.withholdingTaxStatus !== 'completed' || (this.calculateDaysSinceUpdate() > 3 && !this.isJuristicCustomer)
    },
    isRejectStatus () {
      return this.status === 'rejected'
    },
    isDeletedStatus() {
      return this.status === 'deleted'
    },
    isCancelByUser () {
      return this.deletedType === 'userCancel'
    }
  },
  methods: {
    toggleIsExpanded () {
      this.isExpanded = !this.isExpanded
    },
    attachTaxDocument () {
      window.dataLayer = window.dataLayer || []
      window.dataLayer.push({
        event: 'click_tax_btn_juristic_person',
        category: 'user_action',
        label: 'tax_btn_juristic_person',
        value: 1,
        userId: this.lineUid,
        premium_id: this.activeLineOfficialAccount.premiumId || null,
        basic_id: this.activeLineOfficialAccount.basicId || null,
        customer_status: this.activeLineOfficialAccount.isOldAccount ? 'existing' : 'new'
      })

      window.location.href = `/upload-withholding-document/${this.orderId}`
    },
    calculateDaysSinceUpdate () {
      const current = new Date(this.updatedAt)
      const end = new Date()
      let workingDays = 0
      // Reset time to compare dates only
      current.setHours(0, 0, 0, 0)
      end.setHours(0, 0, 0, 0)
      while (current < end) {
        current.setDate(current.getDate() + 1)
        const dayOfWeek = current.getDay()
        // Count only weekdays (not Saturday or Sunday)
        if (dayOfWeek !== 0 && dayOfWeek !== 6) {
          workingDays += 1
        }
      }
      return workingDays
    }
  }
}
</script>

<style scoped>
.cursor-pointer {
  cursor: pointer;
}
</style>
